import { Crown, Shield, ClipboardCheck, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TeamRole } from '@/features/review/types';

// =========================================
// Role definitions
// =========================================

interface RoleOption {
  value: TeamRole;
  label: string;
  description: string;
  icon: typeof Crown;
  color: string;
}

const ROLES: RoleOption[] = [
  {
    value: 'admin',
    label: 'Admin',
    description: 'Can manage members and settings',
    icon: Shield,
    color: 'text-indigo-600',
  },
  {
    value: 'reviewer',
    label: 'Reviewer',
    description: 'Can review and make decisions',
    icon: ClipboardCheck,
    color: 'text-emerald-600',
  },
  {
    value: 'viewer',
    label: 'Viewer',
    description: 'Read-only access to reviews',
    icon: Eye,
    color: 'text-slate-600',
  },
];

// =========================================
// Props
// =========================================

interface RoleSelectorProps {
  value: TeamRole;
  onChange: (role: TeamRole) => void;
  disabled?: boolean;
  excludeOwner?: boolean;
}

// =========================================
// RoleSelector Component
// =========================================

export function RoleSelector({
  value,
  onChange,
  disabled = false,
  excludeOwner = true,
}: RoleSelectorProps) {
  const options = excludeOwner
    ? ROLES
    : [
        {
          value: 'owner' as TeamRole,
          label: 'Owner',
          description: 'Full control over the team',
          icon: Crown,
          color: 'text-amber-600',
        },
        ...ROLES,
      ];

  return (
    <Select
      value={value}
      onValueChange={(v) => onChange(v as TeamRole)}
      disabled={disabled}
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder="Select a role" />
      </SelectTrigger>
      <SelectContent>
        {options.map((role) => {
          const Icon = role.icon;
          return (
            <SelectItem key={role.value} value={role.value}>
              <div className="flex items-center gap-2">
                <Icon className={cn('h-4 w-4', role.color)} />
                <div>
                  <span className="font-medium">{role.label}</span>
                  <span className="ml-2 text-xs text-slate-500">
                    {role.description}
                  </span>
                </div>
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
